import React, { useCallback, useEffect, useRef } from 'react';
import useTimeline from '@src/hooks/useTimeline';
import useLiveTime from '@src/hooks/useLiveTime';
import useTimeLabel from '@src/hooks/useTimeLabel';
import { formatTimelineTime } from '@src/services/timeline';
import { GUTTER_WIDTH } from '../TimelineGrid';
import {
  StyledChip,
  StyledOverlay,
  StyledPlayheadPos,
  StyledRulerSeek,
  StyledScrollShift,
} from './index.style';

/**
 * The playhead's TIME CHIP + the ruler seek strip, rendered as an overlay ABOVE
 * the grid (gutter + viewport). The line itself lives inside the scrolling
 * content (see PlayheadLine); the chip is kept here so the centred label can
 * overlap the gutter at the edges (per Figma) WITHOUT being clipped by the
 * viewport's overflow.
 *
 * The chip stays glued to the (natively-scrolled) line because scroll updates
 * are pushed to subscribers synchronously (see TimelineProvider.setScrollLeft /
 * scrollViewportBy) — the shift here moves in the same frame as the content, so
 * no drift. It hides once the line leaves the viewport. Seek is owned by the
 * parent and threaded in via `onSeekPointerDown`.
 *
 * Position is written to the DOM (per animation frame while playing, see
 * useLiveTime); only the label text re-renders, at the rate the time
 * arrives.
 */
const PlayheadOverlay = ({
  sec = 0,
  playing = false,
  getSec,
  onSeekPointerDown,
}) => {
  const { secToPx, subscribeScroll, scrollRef, viewportWidth } = useTimeline();
  const shiftRef = useRef(null);
  const posRef = useRef(null);
  const [chipRef, drawChip] = useTimeLabel();
  // Content-x of the line (includes START_OFFSET), in a ref so `apply` doesn't
  // change every frame — it would re-subscribe to scroll each time.
  const lineXRef = useRef(0);
  // Last values written to the DOM — `apply` runs every frame while playing, so
  // skip any write that would set what is already there.
  const shiftedToRef = useRef(null);
  const visibleRef = useRef(null);

  const apply = useCallback(
    (scrollLeft) => {
      const lineX = lineXRef.current;
      // The shift only moves on scroll, but `apply` also runs every frame while
      // playing — skip the write when it would set the same value.
      if (shiftRef.current && scrollLeft !== shiftedToRef.current) {
        shiftedToRef.current = scrollLeft;
        shiftRef.current.style.transform = `translateX(${-scrollLeft}px)`;
      }
      if (posRef.current) {
        const x = GUTTER_WIDTH + lineX;
        posRef.current.style.transform = `translateX(${x}px)`;
        // Measured width from the context — reading it off the DOM here would
        // force a layout on every frame. 1px tolerance so the chip stays visible
        // when the line sits exactly at an edge (sub-pixel rounding).
        const offset = lineX - scrollLeft;
        const visible = offset >= -1 && offset <= viewportWidth + 1;
        if (visible !== visibleRef.current) {
          visibleRef.current = visible;
          posRef.current.style.visibility = visible ? 'visible' : 'hidden';
        }
      }
    },
    [viewportWidth],
  );

  // Re-created on zoom (secToPx changes), which re-places the chip.
  const draw = useCallback(
    (t) => {
      lineXRef.current = secToPx(t);
      apply(scrollRef.current);
      drawChip(t);
    },
    [secToPx, apply, scrollRef, drawChip],
  );
  useLiveTime(sec, playing, draw, getSec);

  useEffect(() => subscribeScroll(apply), [subscribeScroll, apply]);

  return (
    <StyledOverlay>
      <StyledRulerSeek onPointerDown={onSeekPointerDown} />
      <StyledScrollShift ref={shiftRef}>
        <StyledPlayheadPos ref={posRef}>
          <StyledChip ref={chipRef} onPointerDown={onSeekPointerDown}>
            {formatTimelineTime(sec)}
          </StyledChip>
        </StyledPlayheadPos>
      </StyledScrollShift>
    </StyledOverlay>
  );
};

export default PlayheadOverlay;
